import React, { useEffect, useRef } from "react";
import {
  motion,
  useMotionValue,
  useMotionTemplate,
  useAnimationFrame,
} from "framer-motion";

import "./InfiniteGridBackground.css";

const GridPattern = ({ offsetX, offsetY, size, patternId }) => (
  <svg className="infinite-grid-svg" aria-hidden="true">
    <defs>
      <motion.pattern
        id={patternId}
        width={size}
        height={size}
        patternUnits="userSpaceOnUse"
        x={offsetX}
        y={offsetY}
      >
        <path
          d={`M ${size} 0 L 0 0 0 ${size}`}
          fill="none"
          strokeWidth="1"
          className="infinite-grid-line"
        />
      </motion.pattern>
    </defs>
    <rect width="100%" height="100%" fill={`url(#${patternId})`} />
  </svg>
);

export default function InfiniteGridBackground({
  gridSize = 44,
  speedX = 0.2,
  speedY = 0.2,
  maskRadius = 260,
}) {
  const containerRef = useRef(null);
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);
  const gridOffsetX = useMotionValue(0);
  const gridOffsetY = useMotionValue(0);

  useAnimationFrame(() => {
    gridOffsetX.set((gridOffsetX.get() + speedX) % gridSize);
    gridOffsetY.set((gridOffsetY.get() + speedY) % gridSize);
  });

  const maskImage = useMotionTemplate`radial-gradient(${maskRadius}px circle at ${mouseX}px ${mouseY}px, black, transparent)`;

  useEffect(() => {
    const handleMouseMove = (event) => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      const rect = container.getBoundingClientRect();
      mouseX.set(event.clientX - rect.left);
      mouseY.set(event.clientY - rect.top);
    };

    const handleMouseLeave = () => {
      const container = containerRef.current;
      if (!container) {
        return;
      }
      mouseX.set(container.clientWidth / 2);
      mouseY.set(container.clientHeight / 2);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseout", handleMouseLeave);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseout", handleMouseLeave);
    };
  }, [mouseX, mouseY]);

  return (
    <div className="infinite-grid-layer" ref={containerRef}>
      <div className="infinite-grid-glow-layer" aria-hidden="true">
        <div className="infinite-grid-glow infinite-grid-glow-right-top-large" />
        <div className="infinite-grid-glow infinite-grid-glow-right-top-small" />
        <div className="infinite-grid-glow infinite-grid-glow-left-bottom-large" />
      </div>

      <div className="infinite-grid-base">
        <GridPattern
          offsetX={gridOffsetX}
          offsetY={gridOffsetY}
          size={gridSize}
          patternId="home-grid-base"
        />
      </div>

      <motion.div
        className="infinite-grid-highlight"
        style={{ maskImage, WebkitMaskImage: maskImage }}
      >
        <GridPattern
          offsetX={gridOffsetX}
          offsetY={gridOffsetY}
          size={gridSize}
          patternId="home-grid-highlight"
        />
      </motion.div>
    </div>
  );
}
